import { toast } from '@heroui/react';
/**
 * 成员管理：排版与组件用法对齐 {@link PageDataGrid}（工具条 / 筛选芯片 / DataGrid / 分页），
 * 本文件自行拼装，不引用 PageDataGrid 组件。
 */
import { useCallback, useMemo, useState } from 'react';
import type { Selection, SortDescriptor } from 'react-aria-components';
import { useAppSelector } from '@hs/shared/stores/hooks';
import { handleTRPCError } from '@hs/shared';

import type { SortingState, PaginationState } from '@hs/ui';
import { cn } from '@heroui/react';
import {
  Avatar,
  Button,
  Chip,
  Description,
  Dropdown,
  Input,
  Label,
  ListBox,
  Modal,
  Pagination,
  SearchField,
  Separator,
  TextField,
} from '@heroui/react';
import { DataGrid, InlineSelect } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { Funnel, LayoutColumns3, Sliders, Xmark } from '@gravity-ui/icons';
import { IconRefresh as RefreshCw, IconDotsVertical as MoreVertical } from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { PeopleSelectDialog } from '@/components/PeopleSelectDialog';
import { PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID } from '@/components/layouts/page/page-data-grid-types';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const ENUM_FILTER_ALL = '__all__';

const SORT_FIELDS: { id: string; label: string }[] = [
  { id: 'joinedAt', label: '加入时间' },
  { id: 'status', label: '状态' },
];

const COLUMN_PICKER: { id: string; label: string }[] = [
  { id: 'user', label: '用户' },
  { id: 'role', label: '角色' },
  { id: 'status', label: '状态' },
  { id: 'person', label: '人员关连' },
  { id: 'wechat', label: '绑定微信' },
  { id: 'joinedAt', label: '加入时间' },
];

function sortingToDescriptor(sorting: SortingState): SortDescriptor | undefined {
  const s = sorting[0];
  if (!s) return undefined;
  return {
    column: s.id,
    direction: s.desc ? 'descending' : 'ascending',
  };
}

function descriptorToSorting(descriptor: SortDescriptor | undefined): SortingState {
  if (!descriptor?.column) return [];
  return [
    {
      id: String(descriptor.column),
      desc: descriptor.direction === 'descending',
    },
  ];
}

function buildPaginationPages(totalPages: number, safePage: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push('ellipsis');
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }
  return pages;
}

type SpaceMemberRow = {
  id: string;
  userId: string;
  roleIds?: string[];
  status: string;
  joinedAt: string;
  user?: {
    id?: string | null;
    nickname?: string | null;
    realname?: string | null;
    username?: string | null;
    avatarUrl?: string | null;
    openids?: Record<string, string>;
  } | null;
  roles?: { name?: string | null }[] | null;
  person?: { id?: string; fullName?: string | null } | null;
};

const DEFAULT_PAGE_SIZE = 10;

export function MemberManagementPanel() {
  const user = useAppSelector((state) => state.auth.user);
  const spaceId = user?.spaces?.[0]?.id;
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);

  const [sorting, setSorting] = useState<SortingState>([{ id: 'joinedAt', desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [personFilter, setPersonFilter] = useState<string>(ENUM_FILTER_ALL);
  const [wechatFilter, setWechatFilter] = useState<string>(ENUM_FILTER_ALL);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<Selection>('all');

  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);
  const [resetPasswordNewPassword, setResetPasswordNewPassword] = useState('');
  const [linkPersonOpen, setLinkPersonOpen] = useState(false);
  const [linkPersonTarget, setLinkPersonTarget] = useState<SpaceMemberRow | null>(null);

  const sortParam = useMemo(() => {
    if (sorting.length === 0) return undefined;
    return sorting
      .map((s: { id: string; desc: boolean }) => `${s.id}:${s.desc ? 'desc' : 'asc'}`)
      .join(',');
  }, [sorting]);

  const personBound = useMemo(() => {
    if (personFilter === ENUM_FILTER_ALL) return undefined;
    return personFilter === 'true' ? true : personFilter === 'false' ? false : undefined;
  }, [personFilter]);

  const userWechatBound = useMemo(() => {
    if (wechatFilter === ENUM_FILTER_ALL) return undefined;
    return wechatFilter === 'true' ? true : wechatFilter === 'false' ? false : undefined;
  }, [wechatFilter]);

  const { data, isLoading, refetch } = trpc.spaceMembers.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: sortParam ? sortParam.split(':')[0] : undefined,
      sortOrder: sortParam?.includes('desc') ? 'desc' : 'asc',
      keyword: searchKeyword.trim() || undefined,
      personBound,
      userWechatBound,
    },
    { enabled: isHydrated && isAuthenticated && !!spaceId }
  );

  const members = (data?.items ?? []) as SpaceMemberRow[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / (pagination.pageSize || DEFAULT_PAGE_SIZE))),
    [total, pagination.pageSize]
  );

  const resetPasswordMutation = trpc.users.resetPassword.useMutation({
    onSuccess: () => {
      toast.success('密码已重置');
      setResetPasswordOpen(false);
      setResetPasswordTarget(null);
      setResetPasswordNewPassword('');
      void refetch();
    },
    onError: (e) => {
      const errorInfo = handleTRPCError(e);
      toast.danger(errorInfo.message || '重置失败');
    },
  });

  const peopleUpdateMutation = trpc.people.update.useMutation({
    onSuccess: () => {
      toast.success('人员关连已更新');
      setLinkPersonOpen(false);
      setLinkPersonTarget(null);
      void refetch();
    },
    onError: (e) => {
      const errorInfo = handleTRPCError(e);
      toast.danger(errorInfo.message || '更新失败');
    },
  });

  const getUserDisplayName = useCallback((member: SpaceMemberRow) => {
    const u = member.user;
    if (u?.nickname) return u.nickname;
    if (u?.realname) return u.realname;
    if (u?.username) return u.username;
    return '未知用户';
  }, []);

  const formatDate = useCallback((dateString: string | null | undefined) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const getStatusChip = useCallback((status: string | undefined) => {
    const statusMap: Record<string, { label: string; color: 'success' | 'warning' | 'default' }> = {
      ACTIVE: { label: '激活', color: 'success' },
      PENDING: { label: '待审核', color: 'warning' },
    };
    const s = statusMap[status || ''] ?? { label: status || '未知', color: 'default' as const };
    return (
      <Chip color={s.color} size="sm" variant="soft">
        <Chip.Label>{s.label}</Chip.Label>
      </Chip>
    );
  }, []);

  const resolveRowId = useCallback((row: SpaceMemberRow) => {
    if (row.user?.id != null) return String(row.user.id);
    return String(row.id);
  }, []);

  const renderActions = useCallback(
    (member: SpaceMemberRow) => (
      <div className="flex justify-center">
        <Dropdown>
          <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
            <MoreVertical className="size-4" stroke={1.5} />
          </Button>
          <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
            <Dropdown.Menu>
              {member.person == null ? (
                <Dropdown.Item
                  id="link"
                  textValue="人员关连"
                  onAction={() => {
                    setLinkPersonTarget(member);
                    setLinkPersonOpen(true);
                  }}
                >
                  <Label>人员关连</Label>
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  id="unlink"
                  textValue="解除人员关连"
                  variant="danger"
                  onAction={() => {
                    if (member.person?.id) {
                      peopleUpdateMutation.mutate({
                        id: member.person.id,
                        data: { userId: null },
                      });
                    }
                  }}
                >
                  <Label>解除人员关连</Label>
                </Dropdown.Item>
              )}
              <Dropdown.Item
                id="reset-pwd"
                textValue="重置密码"
                onAction={() => {
                  const uid = member.user?.id;
                  if (uid) {
                    setResetPasswordTarget({
                      userId: uid,
                      displayName: getUserDisplayName(member),
                    });
                    setResetPasswordNewPassword('');
                    setResetPasswordOpen(true);
                  }
                }}
              >
                <Label>重置密码</Label>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </div>
    ),
    [getUserDisplayName, peopleUpdateMutation]
  );

  const allDataColumns = useMemo<DataGridColumn<SpaceMemberRow>[]>(
    () => [
      {
        id: 'user',
        accessorKey: 'userId',
        header: '用户',
        minWidth: 260,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => {
          const u = row.user;
          const nickname = u?.nickname ?? '';
          const username = u?.username ?? '—';
          const initial = (nickname || username).charAt(0).toUpperCase() || '?';
          return (
            <div className="flex items-center gap-3">
              <Avatar className="size-8 shrink-0" variant="soft">
                {u?.avatarUrl ? <Avatar.Image src={u.avatarUrl} alt={nickname || username} /> : null}
                <Avatar.Fallback>{initial}</Avatar.Fallback>
              </Avatar>
              <div className="flex min-w-0 flex-col">
                <span className="text-sm">{nickname ? nickname : '昵称未设置'}</span>
                <span className="text-muted-foreground truncate text-xs">{username}</span>
              </div>
            </div>
          );
        },
      },
      {
        id: 'role',
        accessorKey: 'roleIds',
        header: '角色',
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => {
          const names = row.roles?.map((r) => r.name).filter(Boolean) ?? [];
          return (
            <span className="text-sm">{names.length > 0 ? names.join('、') : '—'}</span>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: '状态',
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => getStatusChip(row.status),
      },
      {
        id: 'person',
        accessorKey: 'userId',
        header: '人员关连',
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">{row.person?.fullName ?? '未关连'}</span>
        ),
      },
      {
        id: 'wechat',
        accessorKey: 'userId',
        header: '绑定微信',
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => {
          const openids = row.user?.openids;
          return (
            <span className="text-muted-foreground text-sm">{openids?.wechat ? '已绑定' : '未绑定'}</span>
          );
        },
      },
      {
        id: 'joinedAt',
        accessorKey: 'joinedAt',
        header: '加入时间',
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => formatDate(row.joinedAt),
      },
    ],
    [formatDate, getStatusChip]
  );

  const actionsColumn = useMemo<DataGridColumn<SpaceMemberRow>>(
    () => ({
      id: PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID,
      header: '',
      align: 'end',
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      allowsSorting: false,
      allowsResizing: false,
      pinned: 'end',
      cell: (item) => (
        <div className="flex min-w-0 justify-end gap-1 overflow-hidden">{renderActions(item)}</div>
      ),
    }),
    [renderActions]
  );

  const visibleDataColumnIds = useMemo(() => {
    const allIds = COLUMN_PICKER.map((c) => c.id);
    if (visibleColumnKeys === 'all') return new Set(allIds);
    const s = visibleColumnKeys as Set<string>;
    if (s.size === 0) return new Set(allIds);
    const next = new Set<string>();
    for (const id of allIds) {
      if (s.has(id)) next.add(id);
    }
    return next.size > 0 ? next : new Set(allIds);
  }, [visibleColumnKeys]);

  const columns = useMemo(() => {
    const dataCols = allDataColumns.filter((c) => visibleDataColumnIds.has(String(c.id)));
    return [...dataCols, actionsColumn];
  }, [allDataColumns, visibleDataColumnIds, actionsColumn]);

  const dataGridColumnSignature = useMemo(() => {
    const sorted = [...visibleDataColumnIds].sort().join('\u001f');
    return [sorted, PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID].join('\u001e');
  }, [visibleDataColumnIds]);

  const sortDescriptor = useMemo(() => sortingToDescriptor(sorting), [sorting]);

  const sortableColumnIds = useMemo(() => new Set(['joinedAt', 'status']), []);

  const columnsWithRowHeader = useMemo(() => {
    const first = columns.find((c) => c.id !== PAGE_DATA_GRID_ROW_ACTIONS_COLUMN_ID);
    const fh = first?.id;
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === fh,
      allowsSorting: sortableColumnIds.has(String(c.id)) ? true : (c.allowsSorting ?? false),
    }));
  }, [columns, sortableColumnIds]);

  const handleSortChange = useCallback((d: SortDescriptor) => {
    setSorting(descriptorToSorting(d));
  }, []);

  const columnPickerSelectedKeys = useMemo((): Selection => {
    if (visibleColumnKeys === 'all') return new Set(COLUMN_PICKER.map((c) => c.id));
    const s = visibleColumnKeys as Set<string>;
    if (s.size === 0) return new Set(COLUMN_PICKER.map((c) => c.id));
    return s;
  }, [visibleColumnKeys]);

  const handleRefresh = useCallback(() => void refetch(), [refetch]);

  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = useMemo(
    () => buildPaginationPages(pageCount, safePage),
    [pageCount, safePage]
  );

  const pageSizes = [10, 20, 50, 100];

  const hasActiveFilters =
    !!searchKeyword.trim() ||
    personFilter !== ENUM_FILTER_ALL ||
    wechatFilter !== ENUM_FILTER_ALL;

  if (!spaceId) {
    return (
      <div className="flex flex-1 flex-col overflow-y-auto p-6">
        <p className="text-muted-foreground text-sm">请先选择一个空间</p>
      </div>
    );
  }

  return (
    <>
      <div className={cn('flex min-h-0 w-full flex-1 flex-col bg-background p-4')}>
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              'grid min-h-0 min-w-0 w-full flex-1 gap-y-3',
              'grid-rows-[auto_minmax(0,1fr)_auto]'
            )}
            data-slot="member-grid-main"
          >
            <div className="relative z-10 flex min-w-0 shrink-0 flex-col gap-3">
              <div
                className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2"
                data-slot="member-toolbar"
              >
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <SearchField
                    aria-label="搜索成员"
                    value={searchKeyword}
                    onChange={(v) => {
                      setSearchKeyword(v);
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                  >
                    <SearchField.Group>
                      <SearchField.SearchIcon />
                      <SearchField.Input
                        className="w-[min(100%,220px)] min-w-[160px]"
                        placeholder="搜索用户名或昵称"
                      />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>

                  <Dropdown>
                    <Button size="sm" variant="secondary">
                      <Funnel />
                      关连人员
                    </Button>
                    <Dropdown.Popover>
                      <Dropdown.Menu
                        selectedKeys={new Set([personFilter])}
                        selectionMode="single"
                        onSelectionChange={(keys) => {
                          const key = [...keys][0] as string | undefined;
                          setPersonFilter(key ?? ENUM_FILTER_ALL);
                          setPagination((p) => ({ ...p, pageIndex: 0 }));
                        }}
                      >
                        <Dropdown.Item id={ENUM_FILTER_ALL} textValue="全部">
                          <Label>全部</Label>
                          <Dropdown.ItemIndicator />
                        </Dropdown.Item>
                        <Dropdown.Item id="true" textValue="已关连人员">
                          <Label>已关连人员</Label>
                          <Dropdown.ItemIndicator />
                        </Dropdown.Item>
                        <Dropdown.Item id="false" textValue="未关连人员">
                          <Label>未关连人员</Label>
                          <Dropdown.ItemIndicator />
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>

                  <Dropdown>
                    <Button size="sm" variant="secondary">
                      <Funnel />
                      微信绑定
                    </Button>
                    <Dropdown.Popover>
                      <Dropdown.Menu
                        selectedKeys={new Set([wechatFilter])}
                        selectionMode="single"
                        onSelectionChange={(keys) => {
                          const key = [...keys][0] as string | undefined;
                          setWechatFilter(key ?? ENUM_FILTER_ALL);
                          setPagination((p) => ({ ...p, pageIndex: 0 }));
                        }}
                      >
                        <Dropdown.Item id={ENUM_FILTER_ALL} textValue="全部">
                          <Label>全部</Label>
                          <Dropdown.ItemIndicator />
                        </Dropdown.Item>
                        <Dropdown.Item id="true" textValue="已绑定微信">
                          <Label>已绑定微信</Label>
                          <Dropdown.ItemIndicator />
                        </Dropdown.Item>
                        <Dropdown.Item id="false" textValue="未绑定微信">
                          <Label>未绑定微信</Label>
                          <Dropdown.ItemIndicator />
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>

                  <Dropdown>
                    <Button size="sm" variant="secondary">
                      <Sliders />
                      排序
                    </Button>
                    <Dropdown.Popover>
                      <Dropdown.Menu
                        selectedKeys={
                          sortDescriptor?.column
                            ? new Set([String(sortDescriptor.column)])
                            : new Set()
                        }
                        selectionMode="single"
                        onSelectionChange={(keys) => {
                          const key = [...keys][0] as string | undefined;
                          if (!key) return;
                          const prevCol = sorting[0]?.id;
                          const prevDesc = sorting[0]?.desc;
                          const same = prevCol === key;
                          setSorting([
                            {
                              id: key,
                              desc: same ? !prevDesc : false,
                            },
                          ]);
                          setPagination((p) => ({ ...p, pageIndex: 0 }));
                        }}
                      >
                        {SORT_FIELDS.map((f) => (
                          <Dropdown.Item key={f.id} id={f.id} textValue={f.label}>
                            <Label>{f.label}</Label>
                            <Dropdown.ItemIndicator />
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>

                  <Dropdown>
                    <Button size="sm" variant="secondary">
                      <LayoutColumns3 />
                      列显示
                    </Button>
                    <Dropdown.Popover>
                      <Dropdown.Menu
                        disallowEmptySelection
                        selectedKeys={columnPickerSelectedKeys}
                        selectionMode="multiple"
                        onSelectionChange={setVisibleColumnKeys}
                      >
                        {COLUMN_PICKER.map((f) => (
                          <Dropdown.Item key={f.id} id={f.id} textValue={f.label}>
                            <Label>{f.label}</Label>
                            <Dropdown.ItemIndicator />
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>

                <div
                  className="flex shrink-0 flex-wrap items-center justify-end gap-2"
                  data-slot="member-action-toolbar"
                >
                  <Button variant="secondary" onPress={handleRefresh}>
                    <RefreshCw className="size-4" stroke={1.5} />
                    刷新
                  </Button>
                </div>
              </div>

              {hasActiveFilters ? (
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {!!searchKeyword.trim() ? (
                    <Chip size="sm" variant="secondary">
                      <Chip.Label>搜索: {searchKeyword}</Chip.Label>
                      <button
                        type="button"
                        aria-label="清除搜索"
                        className="text-muted hover:text-foreground ml-0.5 inline-flex cursor-pointer items-center"
                        onClick={() => {
                          setSearchKeyword('');
                          setPagination((p) => ({ ...p, pageIndex: 0 }));
                        }}
                      >
                        <Xmark className="size-3" />
                      </button>
                    </Chip>
                  ) : null}
                  {personFilter !== ENUM_FILTER_ALL ? (
                    <Chip size="sm" variant="secondary">
                      <Chip.Label>
                        关连人员: {personFilter === 'true' ? '已关连' : '未关连'}
                      </Chip.Label>
                      <button
                        type="button"
                        aria-label="清除关连人员筛选"
                        className="text-muted hover:text-foreground ml-0.5 inline-flex cursor-pointer items-center"
                        onClick={() => setPersonFilter(ENUM_FILTER_ALL)}
                      >
                        <Xmark className="size-3" />
                      </button>
                    </Chip>
                  ) : null}
                  {wechatFilter !== ENUM_FILTER_ALL ? (
                    <Chip size="sm" variant="secondary">
                      <Chip.Label>
                        微信绑定: {wechatFilter === 'true' ? '已绑定' : '未绑定'}
                      </Chip.Label>
                      <button
                        type="button"
                        aria-label="清除微信筛选"
                        className="text-muted hover:text-foreground ml-0.5 inline-flex cursor-pointer items-center"
                        onClick={() => setWechatFilter(ENUM_FILTER_ALL)}
                      >
                        <Xmark className="size-3" />
                      </button>
                    </Chip>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    onPress={() => {
                      setSearchKeyword('');
                      setPersonFilter(ENUM_FILTER_ALL);
                      setWechatFilter(ENUM_FILTER_ALL);
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                  >
                    清除全部
                  </Button>
                </div>
              ) : null}
            </div>

            <div className="relative min-h-0 min-w-0 overflow-hidden">
              <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
                {isLoading ? (
                  <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                    加载中…
                  </div>
                ) : null}
                <DataGrid<SpaceMemberRow>
                  key={dataGridColumnSignature}
                  allowsColumnResize
                  aria-label="空间成员列表"
                  className="flex h-full min-h-0 min-w-0 flex-col"
                  columns={columnsWithRowHeader}
                  contentClassName="min-w-0"
                  data={members}
                  getRowId={(item) => resolveRowId(item)}
                  renderEmptyState={() => (
                    <div className="text-muted-foreground py-8 text-center text-sm">暂无成员数据</div>
                  )}
                  scrollContainerClassName={cn(
                    'h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain',
                    '[&_[data-slot=table-resizable-container]]:overflow-visible',
                    '[&_[data-slot=table-content]]:!overflow-visible',
                    '[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20',
                    '[&_th]:min-w-0 [&_td]:min-w-0',
                    '[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0'
                  )}
                  selectionMode="none"
                  showSelectionCheckboxes={false}
                  sortDescriptor={sortDescriptor}
                  variant="primary"
                  onSortChange={handleSortChange}
                />
              </div>
            </div>

            <div
              className="text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto text-sm"
              data-slot="member-pagination"
            >
              <div className="flex w-fit shrink-0 items-center">
                <Pagination className="w-fit max-w-none" size="sm">
                  <Pagination.Content className="flex w-fit min-w-0">
                    <Pagination.Item>
                      <Pagination.Previous
                        isDisabled={safePage <= 1}
                        onPress={() =>
                          setPagination((p) => ({
                            ...p,
                            pageIndex: Math.max(0, p.pageIndex - 1),
                          }))
                        }
                      >
                        <Pagination.PreviousIcon />
                      </Pagination.Previous>
                    </Pagination.Item>
                    {paginationPages.map((p, i) =>
                      p === 'ellipsis' ? (
                        <Pagination.Item key={`e-${i}`}>
                          <Pagination.Ellipsis />
                        </Pagination.Item>
                      ) : (
                        <Pagination.Item key={p}>
                          <Pagination.Link
                            isActive={p === safePage}
                            onPress={() =>
                              setPagination((prev) => ({
                                ...prev,
                                pageIndex: (p as number) - 1,
                              }))
                            }
                          >
                            {p}
                          </Pagination.Link>
                        </Pagination.Item>
                      )
                    )}
                    <Pagination.Item>
                      <Pagination.Next
                        isDisabled={safePage >= pageCount}
                        onPress={() =>
                          setPagination((p) => ({
                            ...p,
                            pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                          }))
                        }
                      >
                        <Pagination.NextIcon />
                      </Pagination.Next>
                    </Pagination.Item>
                  </Pagination.Content>
                </Pagination>
              </div>

              <div className="flex h-4 w-fit shrink-0 flex-nowrap items-center gap-3 whitespace-nowrap">
                <InlineSelect
                  aria-label="每页条数"
                  value={String(pagination.pageSize)}
                  onChange={(v) => {
                    if (v) {
                      setPagination((p) => ({
                        ...p,
                        pageSize: Number(v),
                        pageIndex: 0,
                      }));
                    }
                  }}
                >
                  <InlineSelect.Trigger>
                    <span className="text-muted">每页</span>
                    <InlineSelect.Value />
                    <InlineSelect.Indicator />
                  </InlineSelect.Trigger>
                  <InlineSelect.Popover className="w-[88px]">
                    <ListBox>
                      {pageSizes.map((n) => (
                        <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                          {n}
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </InlineSelect.Popover>
                </InlineSelect>
                <Separator className="!h-4 shrink-0" orientation="vertical" />
                <span className="shrink-0 text-muted">
                  第 {total === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
                  {Math.min(safePage * pagination.pageSize, total)} 条，共 {total} 条
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PeopleSelectDialog
        open={linkPersonOpen}
        onOpenChange={(open) => {
          setLinkPersonOpen(open);
          if (!open) setLinkPersonTarget(null);
        }}
        title="人员关连"
        headcount={1}
        userLinkFilter="unlinked"
        onConfirm={(selected) => {
          const userId = linkPersonTarget?.user?.id;
          if (selected.length === 0 || !userId) return;
          peopleUpdateMutation.mutate({
            id: selected[0].peopleId,
            data: { userId },
          });
        }}
      />

      <Modal
        isOpen={resetPasswordOpen}
        onOpenChange={(open) => {
          if (!open) {
            setResetPasswordTarget(null);
            setResetPasswordNewPassword('');
          }
          setResetPasswordOpen(open);
        }}
      >
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" scroll="inside" size="md">
            <Modal.Dialog className="max-h-[90vh]">
              <Modal.Header className="gap-1">
                <Modal.Heading className="text-xl font-medium">重置密码</Modal.Heading>
                <Description className="text-sm">
                  {resetPasswordTarget
                    ? `为「${resetPasswordTarget.displayName}」设置新密码，保存后直接生效。`
                    : ''}
                </Description>
              </Modal.Header>
              <Modal.Body className="gap-4 p-4">
                <TextField className="w-full">
                  <Label className="text-sm font-medium">新密码</Label>
                  <Input
                    autoComplete="new-password"
                    type="password"
                    value={resetPasswordNewPassword}
                    onChange={(e) => setResetPasswordNewPassword(e.target.value)}
                  />
                </TextField>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="ghost"
                  onPress={() => {
                    setResetPasswordOpen(false);
                    setResetPasswordTarget(null);
                    setResetPasswordNewPassword('');
                  }}
                >
                  取消
                </Button>
                <Button
                  isDisabled={!resetPasswordNewPassword.trim() || resetPasswordMutation.isPending}
                  variant="primary"
                  onPress={() => {
                    if (!resetPasswordTarget || !resetPasswordNewPassword.trim()) return;
                    resetPasswordMutation.mutate({
                      id: resetPasswordTarget.userId,
                      newPassword: resetPasswordNewPassword.trim(),
                    });
                  }}
                >
                  {resetPasswordMutation.isPending ? '提交中…' : '确定'}
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </>
  );
}
